/**
 * Hook to initialize the AutomationRuntimeBridge.
 *
 * Connects the Lit-based automation canvas block with the React-based
 * LoggingService / ExecutionService so the block can render run history and
 * live execution state without React context. Mount once per workspace,
 * next to useDatabaseDiscoveryBridge (workspace-side-effects.tsx).
 */

import { useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { AuthService } from '../../cloud';
import { WorkspaceService } from '../../workspace';
import { AutomationConfigApiService } from '../services/automation-config-api';
import {
  clearAutomationRuntimeSource,
  setAutomationRuntimeSource,
} from '../services/automation-runtime-bridge';
import { ExecutionService } from '../services/execution';
import { LoggingService } from '../services/logging';

export function useAutomationRuntimeBridge(): void {
  const configApiService = useService(AutomationConfigApiService);
  const loggingService = useService(LoggingService);
  const executionService = useService(ExecutionService);
  const workspaceService = useService(WorkspaceService);
  const authService = useService(AuthService);

  useEffect(() => {
    setAutomationRuntimeSource(
      configApiService,
      loggingService,
      executionService,
      () => workspaceService.workspace.id,
      () => authService.session.account$.value?.id ?? null
    );
    return () => {
      clearAutomationRuntimeSource();
    };
  }, [
    authService,
    configApiService,
    loggingService,
    executionService,
    workspaceService,
  ]);
}
